/* =============================================================================
   CORE / GUEST-CHANNELS — Gast-Kanäle einzeln schaltbar (S266, phase_196)
   =============================================================================
   Single Source für `settings.guestChannels` — das Objekt, mit dem das
   Restaurant seine Gast-Pfade unabhängig voneinander an- und ausschaltet:

     { reservation: true, reservationPreorder: true,
       takeaway: true, delivery: true, table: true }

   Ersetzt den Master-Schalter `orderingEnabled` (bis S265: EIN Schalter für
   Takeaway, Lieferung, Tisch-QR UND das Cockpit). Jonas-Entscheide 2026-09-28:
     1. Fünf Schalter, keine Pause über allem, keine Doppelung.
     2. Gesperrt wird NUR die Gastseite — das Cockpit erfasst weiterhin alle
        Bestellarten (Telefonbestellung, Walk-In, Liefer-Cockpit).
     3. Der Server (anon-RPCs) lehnt gesperrte Kanäle mit `CHANNEL_DISABLED:<key>`
        ab — kein Weg am UI vorbei.

   Der SQL-Spiegel ist `gg_guest_channel_enabled(p_settings, p_key)` (phase_196):
   fehlender Schlüssel = true. Wer hier eine Regel ändert, ändert beide Stellen.

   Pure — kein DOM, kein Storage. Wird von der Gastseite (customer-form.js),
   dem Tisch-QR-Schloss (table-qr.js), den Settings (settings-rules.js) und
   dem Website-Button (public/embed.js, als eigenständige Kopie der Label-Regel,
   weil embed.js ohne Build auf fremden Websites läuft) benutzt.
============================================================================ */

/** Schlüssel in der Reihenfolge, in der die Settings-Karte sie zeigt. */
export const GUEST_CHANNEL_KEYS = Object.freeze([
  'reservation',
  'reservationPreorder',
  'takeaway',
  'delivery',
  'table'
]);

/** Fehlender Schlüssel = an (defensiv wie das alte COALESCE(..., true)). */
export const GUEST_CHANNEL_DEFAULTS = Object.freeze({
  reservation:         true,
  reservationPreorder: true,
  takeaway:            true,
  delivery:            true,
  table:               true
});

/** Einstiegs-Kanäle der Gastseite in Chip-Reihenfolge (ohne Vorbestellung, ohne Tisch). */
export const GUEST_ENTRY_CHANNELS = Object.freeze(['reservation', 'takeaway', 'delivery']);

const GUEST_CHANNEL_SET = new Set(GUEST_CHANNEL_KEYS);

/** Lesbare Bezeichnungen — Settings-Karte, Toasts, Hinweise. */
export const GUEST_CHANNEL_LABELS = Object.freeze({
  reservation:         'Reservieren',
  reservationPreorder: 'Vorbestellung bei Reservierung',
  takeaway:            'Mitnehmen',
  delivery:            'Lieferung',
  table:               'Bestellen am Tisch'
});

/** `settings.guestChannels` als Objekt — nie null, nie ein Nicht-Objekt. */
function _channels(settings) {
  const gc = settings && settings.guestChannels;
  return (gc && typeof gc === 'object' && !Array.isArray(gc)) ? gc : {};
}

/**
 * Ist der Kanal an? Nur ein explizites `false` sperrt — alles andere (fehlt,
 * null, undefined, sonstiger Müll) gilt als an. Unbekannte Schlüssel → true,
 * damit ein Tippfehler nie still einen Pfad sperrt.
 */
export function guestChannelEnabled(settings, key) {
  if (!GUEST_CHANNEL_SET.has(key)) return true;
  return _channels(settings)[key] !== false;
}

/** Vollständiges Objekt mit allen fünf Schlüsseln (Defaults + gespeicherte Werte). */
export function resolvedGuestChannels(settings) {
  const cur = _channels(settings);
  const out = {};
  GUEST_CHANNEL_KEYS.forEach(k => { out[k] = cur[k] !== false; });
  return out;
}

/** Aktive Einstiegs-Kanäle in Chip-Reihenfolge: reservation, takeaway, delivery. */
export function activeGuestEntryChannels(settings) {
  return GUEST_ENTRY_CHANNELS.filter(k => guestChannelEnabled(settings, k));
}

/** Gibt es überhaupt einen offenen Einstieg für Gäste (ohne Tisch-QR)? */
export function isAnyGuestEntryOpen(settings) {
  return activeGuestEntryChannels(settings).length > 0;
}

/** Service-Typ des Formulars → Kanal-Schlüssel. Unbekannt → null. */
export function channelForServiceType(serviceType) {
  switch (serviceType) {
    case 'dine-in':  return 'reservation';
    case 'takeaway': return 'takeaway';
    case 'delivery': return 'delivery';
    default:         return null;
  }
}

/** Kanal-Schlüssel → Service-Typ der Chips. Unbekannt → null. */
export function serviceTypeForChannel(key) {
  switch (key) {
    case 'reservation': return 'dine-in';
    case 'takeaway':    return 'takeaway';
    case 'delivery':    return 'delivery';
    default:            return null;
  }
}

/**
 * Beschriftung des Gast-Einstiegs (Hero der Gastseite UND Website-Button):
 *   Reservieren an, kein Bestell-Kanal   → „Tisch reservieren"
 *   Reservieren an + Bestell-Kanal       → „Reservieren & Bestellen"
 *   nur Mitnehmen und/oder Lieferung     → „Online bestellen"
 *   alles aus                            → null (Button nicht rendern)
 *
 * public/embed.js trägt dieselbe Regel als Kopie — der Test
 * `guest-channels.test.js` prüft, dass beide dieselben Strings kennen.
 */
export function guestEntryLabel(settings) {
  const res   = guestChannelEnabled(settings, 'reservation');
  const order = guestChannelEnabled(settings, 'takeaway') || guestChannelEnabled(settings, 'delivery');
  if (res && order) return 'Reservieren & Bestellen';
  if (res)          return 'Tisch reservieren';
  if (order)        return 'Online bestellen';
  return null;
}

/**
 * Gast-Meldung, wenn der Server einen Kanal ablehnt (`CHANNEL_DISABLED:<key>`).
 * Die Settings haben sich geändert, während die Gastseite offen war — der
 * Gast bekommt einen Satz, keinen Postgres-Text.
 */
export function guestChannelDisabledMessage(key) {
  switch (key) {
    case 'reservation':         return 'Die Online-Reservierung ist gerade nicht verfügbar.';
    case 'reservationPreorder': return 'Die Vorbestellung zur Reservierung ist gerade nicht verfügbar — bitte ohne Vorbestellung reservieren.';
    case 'takeaway':            return 'Mitnehmen ist gerade nicht verfügbar.';
    case 'delivery':            return 'Lieferung ist gerade nicht verfügbar.';
    case 'table':               return 'Bestellen am Tisch ist gerade nicht verfügbar – bitte wende dich an unser Team.';
    default:                    return 'Dieser Weg ist gerade nicht verfügbar.';
  }
}

/**
 * Zieht den Kanal-Schlüssel aus einem Server-Fehler (`CHANNEL_DISABLED:takeaway …`).
 * Liefert null, wenn es kein Kanal-Fehler ist. Akzeptiert Error-Objekte,
 * Supabase-Fehler ({message, hint, details}) und rohe Strings.
 */
export function channelDisabledKeyFromError(err) {
  if (!err) return null;
  const msg = typeof err === 'string'
    ? err
    : String(err.message || err.hint || err.details || '');
  const m = /CHANNEL_DISABLED:([A-Za-z]+)/.exec(msg);
  if (!m) return null;
  return GUEST_CHANNEL_SET.has(m[1]) ? m[1] : 'unknown';
}

/**
 * Gibt es überhaupt einen Liefer-Weg (eigene Fahrer, Uber-Marktplatz oder
 * Uber-Direct-Fallback)? Reiner Hinweis für die Settings-Karte — der Schalter
 * „Lieferung" bleibt auch ohne Liefer-Weg schaltbar.
 */
export function hasDeliveryPath(settings) {
  const d = (settings && settings.delivery && typeof settings.delivery === 'object') ? settings.delivery : {};
  return d.internal_drivers_enabled !== false
      || d.uber_marketplace_enabled === true
      || d.uber_direct_fallback_enabled === true;
}

// ---------------------------------------------------------------------------
// Backward-Compat: window-Zugriff für Nicht-Modul-Code (Adapter, Inline-Handler).
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.GuestChannels = {
    GUEST_CHANNEL_KEYS,
    GUEST_CHANNEL_DEFAULTS,
    GUEST_CHANNEL_LABELS,
    GUEST_ENTRY_CHANNELS,
    guestChannelEnabled,
    resolvedGuestChannels,
    activeGuestEntryChannels,
    isAnyGuestEntryOpen,
    channelForServiceType,
    serviceTypeForChannel,
    guestEntryLabel,
    guestChannelDisabledMessage,
    channelDisabledKeyFromError,
    hasDeliveryPath
  };
}
